import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Users, MessageSquare, Activity as ActivityIcon, AlertCircle } from 'lucide-react';
import { dashboardAPI, systemAPI, activityAPI } from '../../services/api/adminApi';
import type {
  DashboardMetrics,
  UsageTrend,
  ModelUsage,
  SystemHealth,
  Activity,
} from '../../types/admin';
import { formatDateWithoutYear } from '../../utils/dateFormat';

type TrendPeriod = '7d' | '30d' | '90d';

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<TrendPeriod>('7d');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [usageTrends, setUsageTrends] = useState<UsageTrend[]>([]);
  const [modelUsage, setModelUsage] = useState<ModelUsage[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [metricsRes, trendsRes, modelUsageRes, healthRes, activityRes] = await Promise.all([
        dashboardAPI.getMetrics(),
        dashboardAPI.getUsageTrends(period),
        dashboardAPI.getModelUsage(),
        systemAPI.getHealth(),
        activityAPI.getRecent(20),
      ]);

      setMetrics(metricsRes.data);
      setUsageTrends(trendsRes.data.trends);
      setModelUsage(modelUsageRes.data);
      setSystemHealth(healthRes.data);
      setRecentActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
        <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4'>
          <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>{t('admin.title')}</h1>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-ui-secondary'>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !metrics || !systemHealth) {
    return (
      <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
        <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4'>
          <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>{t('admin.title')}</h1>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-red-600'>{error || 'Failed to load dashboard'}</div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
      {/* Header */}
      <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4 flex-shrink-0'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
          <div>
            <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>{t('admin.title')}</h1>
            <p className='text-sm text-gray-600 mt-1'>{t('admin.dashboardSubtitle')}</p>
          </div>
          <div className='flex items-center space-x-3'>
            <select
              value={period}
              onChange={e => setPeriod(e.target.value as TrendPeriod)}
              className='px-3 py-2 border border-ui-border rounded-ui-md font-body-sm text-ui-charcoal bg-white hover:bg-ui-light transition-colors text-sm sm:text-base w-full sm:w-auto'
            >
              <option value='7d'>{t('admin.dashboard.last7Days')}</option>
              <option value='30d'>{t('admin.dashboard.last30Days')}</option>
              <option value='90d'>{t('admin.dashboard.last90Days')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className='px-4 sm:p-6 space-y-4 sm:space-y-6 flex-1 overflow-y-auto'>
        {/* Metrics Cards */}
        <div className='grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4'>
          <DashboardCard
            icon={<Users size={18} className='text-ui-primary' />}
            label={t('admin.totalUsers')}
            value={metrics.total_users.toLocaleString()}
            trend='+12%'
            trendColor='text-green-600'
          />
          <DashboardCard
            icon={<Shield size={18} className='text-ui-primary' />}
            label={t('admin.activeUsers')}
            value={metrics.active_users.toLocaleString()}
            trend='+8%'
            trendColor='text-green-600'
          />
          <DashboardCard
            icon={<MessageSquare size={18} className='text-ui-primary' />}
            label={t('admin.dashboard.conversations')}
            value={metrics.total_conversations.toLocaleString()}
            trend='+15%'
            trendColor='text-green-600'
          />
          <DashboardCard
            icon={<ActivityIcon size={18} className='text-ui-primary' />}
            label={t('admin.dashboard.engagement')}
            value={`${metrics.engagement_rate || 0}%`}
            trend='-3%'
            trendColor='text-red-600'
          />
        </div>

        {/* Usage Trends Chart */}
        <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3'>
            <h2 className='font-heading-lg font-semibold text-ui-charcoal text-lg sm:text-xl'>
              {t('admin.dashboard.usageTrends')}
            </h2>
            <div className='flex space-x-2'>
              <button
                onClick={() => setPeriod('7d')}
                className={`px-3 py-1 text-sm font-body-sm rounded-ui-md transition-colors ${
                  period === '7d'
                    ? 'bg-ui-primary text-white'
                    : 'text-ui-secondary hover:bg-ui-light'
                }`}
              >
                7d
              </button>
              <button
                onClick={() => setPeriod('30d')}
                className={`px-3 py-1 text-sm font-body-sm rounded-ui-md transition-colors ${
                  period === '30d'
                    ? 'bg-ui-primary text-white'
                    : 'text-ui-secondary hover:bg-ui-light'
                }`}
              >
                30d
              </button>
              <button
                onClick={() => setPeriod('90d')}
                className={`px-3 py-1 text-sm font-body-sm rounded-ui-md transition-colors ${
                  period === '90d'
                    ? 'bg-ui-primary text-white'
                    : 'text-ui-secondary hover:bg-ui-light'
                }`}
              >
                90d
              </button>
            </div>
          </div>

          {/* Simple SVG Chart */}
          <div className='relative h-48 sm:h-56 lg:h-64 overflow-hidden'>
            <svg
              viewBox='0 0 400 100'
              className='w-full h-full'
              preserveAspectRatio='xMidYMid meet'
            >
              {/* Grid lines */}
              {[10, 30, 50, 70, 90].map((y, i) => (
                <line key={i} x1='5' y1={y} x2='395' y2={y} stroke='#E2E8F0' strokeWidth='0.5' />
              ))}

              {/* Message trend line */}
              {usageTrends.length > 0 &&
                (() => {
                  const maxMessages = Math.max(...usageTrends.map(t => t.messages), 1);
                  const minMessages = Math.min(...usageTrends.map(t => t.messages));
                  // Use full range if there's variation, otherwise center the line
                  const range = maxMessages - minMessages;
                  const useFullScale = range > maxMessages * 0.1;

                  // Chart area with padding: x [5, 395], y [10, 90]
                  const chartWidth = 390;
                  const chartHeight = 80;
                  const chartTop = 10;
                  const chartLeft = 5;

                  return (
                    <polyline
                      fill='none'
                      stroke='#3B82F6'
                      strokeWidth='2'
                      points={usageTrends
                        .map((d, i) => {
                          const x =
                            usageTrends.length > 1
                              ? chartLeft + (i / (usageTrends.length - 1)) * chartWidth
                              : 200;
                          // Map data to y range with proper scaling and padding
                          const normalizedValue = useFullScale
                            ? (d.messages - minMessages) / range
                            : d.messages / maxMessages;
                          const y = chartTop + chartHeight - normalizedValue * chartHeight;
                          return `${x},${y}`;
                        })
                        .join(' ')}
                    />
                  );
                })()}

              {/* Data points */}
              {usageTrends.length > 0 &&
                (() => {
                  const maxMessages = Math.max(...usageTrends.map(t => t.messages), 1);
                  const minMessages = Math.min(...usageTrends.map(t => t.messages));
                  const range = maxMessages - minMessages;
                  const useFullScale = range > maxMessages * 0.1;

                  const chartWidth = 390;
                  const chartHeight = 80;
                  const chartTop = 10;
                  const chartLeft = 5;

                  return usageTrends.map((d, i) => {
                    const x =
                      usageTrends.length > 1
                        ? chartLeft + (i / (usageTrends.length - 1)) * chartWidth
                        : 200;
                    const normalizedValue = useFullScale
                      ? (d.messages - minMessages) / range
                      : d.messages / maxMessages;
                    const y = chartTop + chartHeight - normalizedValue * chartHeight;
                    return <circle key={i} cx={x} cy={y} r='3' fill='#3B82F6' />;
                  });
                })()}

              {/* Labels - with better spacing to prevent overlap on wide screens */}
              {usageTrends.map((d, i) => {
                const chartWidth = 390;
                const chartLeft = 5;
                const x =
                  usageTrends.length > 1
                    ? chartLeft + (i / (usageTrends.length - 1)) * chartWidth
                    : 200;
                // Much more aggressive label skipping for wide screens
                const skipInterval =
                  usageTrends.length <= 7
                    ? 1
                    : usageTrends.length <= 14
                      ? 3
                      : usageTrends.length <= 30
                        ? 6
                        : 10;
                if (i % skipInterval !== 0) {
                  return null;
                }
                return (
                  <text
                    key={i}
                    x={x}
                    y='98'
                    textAnchor='middle'
                    className='text-[6px] fill-ui-muted font-body-sm'
                  >
                    {formatDateWithoutYear(d.date)}
                  </text>
                );
              })}
            </svg>
          </div>

          {/* Legend */}
          <div className='flex flex-wrap items-center gap-3 sm:gap-6 mt-6'>
            <div className='flex items-center space-x-2'>
              <div className='w-3 h-3 rounded-full bg-ui-primary'></div>
              <span className='font-body-sm text-ui-secondary text-xs sm:text-sm'>
                {t('admin.dashboard.messages')}
              </span>
            </div>
            <div className='flex items-center space-x-2'>
              <div className='w-3 h-3 rounded-full bg-ui-accent'></div>
              <span className='font-body-sm text-ui-secondary text-xs sm:text-sm'>
                {t('admin.dashboard.tokens')}
              </span>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
          {/* Model Usage */}
          <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
            <div className='mb-4 sm:mb-5'>
              <h2 className='font-heading-lg font-semibold text-ui-charcoal text-base sm:text-lg'>
                {t('admin.dashboard.topModels')}
              </h2>
            </div>

            <div className='space-y-3 sm:space-y-4'>
              {modelUsage.map((model, index) => (
                <div key={index} className='space-y-1.5 sm:space-y-2'>
                  <div className='flex items-center justify-between gap-2'>
                    <span className='font-body-sm font-medium text-ui-charcoal text-xs sm:text-sm truncate'>
                      {model.name}
                    </span>
                    <span className='font-body-sm text-ui-secondary text-xs sm:text-sm flex-shrink-0'>
                      {model.percentage}%
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-1.5 sm:h-2 overflow-hidden'>
                    <div
                      className='bg-ui-primary h-1.5 sm:h-2 rounded-full transition-all duration-300'
                      style={{ width: `${model.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System Health */}
          <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
            <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4 sm:mb-5'>
              <h2 className='font-heading-lg font-semibold text-ui-charcoal text-base sm:text-lg'>
                {t('admin.dashboard.systemHealth')}
              </h2>
              <span
                className={`px-2 sm:px-3 py-1 rounded-full text-xs font-body-sm font-medium whitespace-nowrap ${getStatusColor(systemHealth.status)}`}
              >
                {systemHealth.status === 'healthy'
                  ? t('admin.dashboard.allSystemsOperational')
                  : t('admin.dashboard.issuesDetected')}
              </span>
            </div>

            <div className='space-y-2.5 sm:space-y-3'>
              <HealthItem
                label={t('admin.dashboard.apis')}
                status={systemHealth.apis ? 'operational' : 'error'}
              />
              <HealthItem
                label={t('admin.dashboard.database')}
                status={systemHealth.database ? 'operational' : 'error'}
              />
              <HealthItem
                label={t('admin.dashboard.gpuLoad')}
                status={systemHealth.gpu ? 'operational' : 'elevated'}
              />
              <HealthItem
                label={t('admin.dashboard.storage')}
                status='operational'
                detail={`${systemHealth.storage.percentage}% used`}
              />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className='bg-white border border-ui-border rounded-lg shadow-ui p-4 sm:p-6'>
          <h2 className='font-heading-lg font-semibold text-ui-charcoal mb-4 text-lg sm:text-xl'>
            {t('admin.dashboard.recentActivity')}
          </h2>
          <div className='space-y-3'>
            {recentActivity.map(activity => (
              <div
                key={activity.id}
                className='flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-ui-border last:border-0 gap-1'
              >
                <div className='flex items-center space-x-3'>
                  {activity.type === 'warning' ? (
                    <AlertCircle size={16} className='text-yellow-500 flex-shrink-0' />
                  ) : (
                    <div className='w-2 h-2 rounded-full bg-ui-primary flex-shrink-0' />
                  )}
                  <span className='font-body-sm text-ui-charcoal text-sm sm:text-base'>
                    <span className='font-medium'>{activity.user}</span> {activity.action}
                  </span>
                </div>
                <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>
                  {activity.time_ago}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface DashboardCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend: string;
  trendColor: string;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ icon, label, value, trend, trendColor }) => {
  return (
    <div className='bg-white border border-ui-border rounded-lg shadow-ui p-3 sm:p-4 md:p-6 hover:shadow-ui-md transition-shadow'>
      <div className='flex items-center justify-between mb-2 sm:mb-3 md:mb-4'>
        <div className='w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-blue-50 rounded-lg flex items-center justify-center'>
          {icon}
        </div>
        <span className={`font-body-sm font-medium ${trendColor} text-xs sm:text-xs md:text-sm`}>
          {trend}
        </span>
      </div>
      <div>
        <div className='font-display-xl font-bold text-ui-charcoal text-lg sm:text-xl md:text-3xl'>
          {value}
        </div>
        <div className='font-body-sm text-ui-secondary mt-1 text-xs sm:text-xs md:text-sm'>
          {label}
        </div>
      </div>
    </div>
  );
};

interface HealthItemProps {
  label: string;
  status: 'operational' | 'elevated' | 'error';
  detail?: string;
}

const HealthItem: React.FC<HealthItemProps> = ({ label, status, detail }) => {
  const { t } = useTranslation();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'text-green-600';
      case 'elevated':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return '✓';
      case 'elevated':
        return '⚠';
      case 'error':
        return '✕';
      default:
        return '•';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational':
        return t('admin.dashboard.operational');
      case 'elevated':
        return t('admin.dashboard.elevated');
      case 'error':
        return t('admin.dashboard.error');
      default:
        return status;
    }
  };

  return (
    <div className='flex items-center justify-between'>
      <span className='font-body-sm text-ui-charcoal text-xs sm:text-sm'>{label}</span>
      <div className='flex items-center space-x-1.5 sm:space-x-2'>
        {detail && <span className='font-body-sm text-ui-muted text-xs sm:text-sm'>{detail}</span>}
        <span className={`font-body-sm font-medium text-xs sm:text-sm ${getStatusColor(status)}`}>
          {getStatusIcon(status)} <span className='hidden xs:inline'>{getStatusText(status)}</span>
        </span>
      </div>
    </div>
  );
};

export default AdminDashboard;
